import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { useState, useEffect, useRef } from "react";

function DropoffModal({ dropoffs, portal, onClose }) {
  const mapRef = useRef(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [selected, setSelected] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyC8kOFpMIzNKTgoey7z1yoGhXJOqtGYomg",
  });

// 1. read from localStorage
const disableGoogleMaps =
  localStorage.getItem("disableGoogleMaps") === "true";

// 2. maps are only shown if enabled in settings AND not disabled locally
const mapsEnabled = portal?.data?.settings?.googlemaps === true && !disableGoogleMaps;

const showMap =
  mapsEnabled &&
  isLoaded &&
  !dropoffs.loading &&
  (dropoffs.items?.length ?? 0) > 0;

  // Handle resizing between mobile/desktop
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (mapInstance && dropoffs.items?.length) {
      const bounds = new window.google.maps.LatLngBounds();
      dropoffs.items.forEach((loc) =>
        bounds.extend({ lat: Number(loc.lat), lng: Number(loc.lng) })
      );
      mapInstance.fitBounds(bounds);
    }
  }, [mapInstance, dropoffs.items]);

  return (
    <div
      style={{
        width: isMobile ? "95%" : "50%",
        height: isMobile ? "85%" : "80%",
        maxWidth: 1100,
        background: "#fff",
        borderRadius: 12,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
      }}
    >
      <div
        style={{
          padding: "14px 18px",
          borderBottom: "1px solid #eee",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
          gap: 12,
        }}
      >
        {/* Left: Logo + Title */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {dropoffs.logoURL && (
            <img
              src={dropoffs.logoURL}
              alt="Service Logo"
              style={{
                width: 40,
                height: 40,
                objectFit: "contain",
                borderRadius: 8,
                background: "#fafafa",
              }}
            />
          )}
          <div>
            <h5
              style={{
                margin: 0,
                fontSize: 17,
                fontWeight: 600,
              }}
            >
              Dropoff locations{" "}
              {dropoffs.serviceNameCustomer
                ? `for ${dropoffs.serviceNameCustomer}`
                : ""}
            </h5>
            {dropoffs.serviceCustomerName && (
              <div
                style={{
                  fontSize: 13,
                  color: "#555",
                  marginTop: 2,
                }}
              >
                {dropoffs.serviceCustomerName}
              </div>
            )}
          </div>
        </div>

        {/* Right: Close button */}
        <button
          onClick={onClose}
          style={{
            border: "none",
            background: "transparent",
            fontSize: 26,
            lineHeight: 1,
            cursor: "pointer",
            color: "#333",
            fontWeight: "600",
            padding: "0 4px",
            alignSelf: "flex-start",
          }}
        >
          ×
        </button>
      </div>

      {/* Responsive layout */}
      <div
        style={{
          display: "flex",
          flexDirection: isMobile || !showMap ? "column" : "row",
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* MAP SECTION (only renders when showMap) */}
        {showMap && (
          <div
            ref={mapRef}
            style={{
              flex: isMobile ? "0 0 50%" : "0 0 60%",
              position: "relative",
              background: "#f9f9f9",
              minHeight: isMobile ? 260 : "auto",
              borderBottom: isMobile ? "1px solid #eee" : "none",
            }}
          >
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "100%" }}
              options={{
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
              }}
              onLoad={(map) => setMapInstance(map)}
            >
              {dropoffs.items.map((loc, i) => (
                <Marker
                  key={i}
                  position={{ lat: Number(loc.lat), lng: Number(loc.lng) }}
                  onClick={() => setSelected(loc)}
                />
              ))}
            </GoogleMap>
          </div>
        )}

        {/* LIST SECTION */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: isMobile ? "16px 16px 24px" : "16px 20px",
            // no divider when there’s no map column
            borderLeft: showMap ? "1px solid #eee" : "none",
          }}
        >
          {dropoffs.loading ? (
            <p style={{ color: "#666", fontSize: 14 }}>
              Loading drop-off locations…
            </p>
          ) : dropoffs.error ? (
            <div
              style={{
                backgroundColor: "#f8d7da",
                color: "#842029",
                border: "1px solid #f5c2c7",
                borderRadius: 8,
                padding: "10px 12px",
                fontSize: 13,
              }}
            >
              {dropoffs.error}
            </div>
          ) : (
            (dropoffs.items ?? []).map((loc, i) => (
              <div
                key={i}
                onClick={() => {
                  setSelected(loc);
                  if (mapInstance) {
                    mapInstance.panTo({
                      lat: Number(loc.lat),
                      lng: Number(loc.lng),
                    });
                    mapInstance.setZoom(13);
                  }
                }}
                style={{
                  cursor: "pointer",
                  paddingBottom: 14,
                  borderBottom: "1px solid #eee",
                  marginBottom: 14,
                }}
              >
                <div
                  style={{
                    color:
                      portal?.data?.settings?.colours?.primary || "#e39408",
                    fontWeight: 700,
                    fontSize: 14,
                    marginBottom: 4,
                  }}
                >
                  {loc.title}
                </div>
                <div style={{ fontSize: 13, color: "#444" }}>{loc.address}</div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default DropoffModal;
